import {Component, Input, OnChanges, OnInit} from '@angular/core';
import {Chart} from 'angular-highcharts';
import {PointOptionsObject, SeriesOptionsType} from 'highcharts';
import {CountryChartData} from '../../../../models/country-chart-data.interface';
import {BENCHMARK_BY_YEAR_AREA_COLOR, BENCHMARK_BY_YEAR_COLORS} from '../../../../../constants/charts';
import {MessageService} from '../../../../../shared/services/message.service';
import {Router} from '@angular/router';
import {AuthService} from '../../../../../core/services/auth.service';
import {formatNumber} from '../../../../utils/number-format-utils';

interface BenchmarkData {
  category: string;
  total: number;
  totalSpecific: number;
  weight: number;
  countryData: CountryChartData[];
}

@Component({
  selector: 'app-benchmark-by-year-bar-chart',
  templateUrl: './benchmark-by-year-bar-chart.component.html',
  styleUrls: ['./benchmark-by-year-bar-chart.component.scss']
})
export class BenchmarkByYearBarChartComponent implements OnInit, OnChanges {

  textBelow!: string;
  chart!: Chart;
  chartData!: SeriesOptionsType[];
  textBelowCodePOM = 'kfcharts.Country.PutOnMarket.BenchmarkByYear.textBelow';
  textBelowCodeCollected = 'kfcharts.Country.Collected.BenchmarkByYear.textBelow';
  isAdmin!: boolean;

  @Input()
  data!: CountryChartData[];
  @Input()
  displayUnits!: string;
  @Input()
  title!: string;
  @Input()
  subtitle!: string;
  @Input()
  countryCategory!: string;
  @Input()
  sort!: string;
  @Input()
  countryCodes!: string[];

  constructor(private readonly messageService: MessageService,
              private readonly router: Router,
              private authService: AuthService) {
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.getUserRoles().includes('WF_ADMIN');
    this.messageService.getDialogInfo(
      this.countryCategory === 'putonmarket' ? this.textBelowCodePOM : this.textBelowCodeCollected).subscribe((data) => {
      if (data !== null) {
        this.textBelow = data.description;
      }
    });
  }

  ngOnChanges(): void {
    if (this.data && this.countryCodes) {
      this.chartData = this.convertToChartData(this.data);
      this.chart = new Chart({
        chart: {
          zoomType: 'xy',
          type: 'column',
          options3d: {
            enabled: true,
            alpha: 7,
            beta: 0,
            depth: 35,
            viewDistance: 15
          }
        },
        colors: BENCHMARK_BY_YEAR_COLORS,
        title: {
          text: this.title
        },
        subtitle: {
          text: this.subtitle
        },
        exporting: {
          tableCaption: this.title + ' ' + this.subtitle
        },
        plotOptions: {
          column: {
            stacking: 'normal',
            dataLabels: {
              enabled: false,
            },
          },
        },
        tooltip: {
          formatter(): string[] | undefined {
            const pointOptions = this.point as PointOptionsObject;
            if (pointOptions.custom?.absolute === 0) {
              return [this.point.name];
            }
            if (!this.point.series.name.includes('average')) {
              return ['<div class="center"><b>' + this.point.name + '</b></div><br><br>',
                '- Total: ' + this.point.total?.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',') + (pointOptions.custom?.displayUnits === 'SPECIFIC' ? ' kg/inh' : ' tons') + '<br>',
                '- ' + this.point.series.name + ' Value: ' + formatNumber(pointOptions.custom?.absolute) + ' tons' + '<br>',
                '- ' + this.point.series.name + ' Specific: ' + (pointOptions.custom?.population ?
                  formatNumber(pointOptions.custom?.absolute * 1000 / pointOptions.custom?.population) + ' kg/inh.' : 'No data')
                + '<br>' +
                '- Population: ' + (pointOptions.custom?.population ?
                  formatNumber(pointOptions.custom?.population, 0) + ' inhabitants' : 'No data') + '<br>',

              ];
            } else {
              return ['<div class="center"><b>' + pointOptions.custom?.title + '</b></div><br>',
                'Value: ' + formatNumber(this.point.y) + (pointOptions.custom?.displayUnits === 'SPECIFIC' ? ' kg/inh' : ' tons') + '<br>',
              ];
            }
          }
        },
        xAxis: {
          // reversed: this.sort === 'desc' ? true : false,
          categories: this.countryCodes,
          title: {
            text: null
          }
        },
        series: this.chartData
      });
    }
  }

  editTextBelow(): void {
    this.router.navigate(
      ['/admin/chart-labels'],
      {state: {code: this.countryCategory === 'putonmarket' ? this.textBelowCodePOM : this.textBelowCodeCollected}}).then(
      () => {
        // do nothing
      }
    );
  }


  private convertToChartData(data: CountryChartData[]): SeriesOptionsType[] {
    this.chart?.ref?.hideData();
    const groupedData: BenchmarkData[] = this.normalizeData(
      this.groupBy(data, (countryChartData: CountryChartData) => countryChartData.keyFigureCategoryName), this.countryCodes);
    const chartData: SeriesOptionsType[] = groupedData.map((countryData, i) => {
      return {
        name: countryData.category,
        type: 'column',
        legendIndex: i,
        data: countryData.countryData.map(item => {
          return {
            y: this.displayUnits === 'SPECIFIC' ? Number(formatNumber((item.absolute * 1000 / item.population))) : item.absolute,
            name: item.countryName,
            custom: {population: item.population, absolute: item.absolute, displayUnits: this.displayUnits},
          };
        })
      };
    });

    const groupedDataByCountry: BenchmarkData[] = this.groupBy(data, (countryChartData: CountryChartData) => countryChartData.countryCode);

    let average = 0;
    let specificAverage = 0;
    let weightedAverage = 0;
    let specificWeightedAverage = 0;
    let totalWeight = 0;

    groupedDataByCountry.forEach(item => {
      average += item.total;
      specificAverage += item.totalSpecific;
      if (item.weight && item.total) {
        weightedAverage += (item.total * item.weight);
        specificWeightedAverage += (item.totalSpecific * item.weight);
        totalWeight += item.weight;
      }
    });

    average = Number((average / this.countryCodes.length).toFixed(2));
    specificAverage = Number((specificAverage / this.countryCodes.length).toFixed(2));
    weightedAverage = Number((weightedAverage / totalWeight).toFixed(2));
    specificWeightedAverage = Number((specificWeightedAverage / totalWeight).toFixed(2));

    chartData.push({
      name: `Overall average/country (${this.displayUnits === 'SPECIFIC' ? formatNumber(specificAverage) : formatNumber(average)} ${this.displayUnits === 'SPECIFIC' ? 'kg/inh' : 'tons'})`,
      type: 'area',
      legendIndex: 998,
      marker: {
        enabled: false,
        states: {
          hover: {
            enabled: false
          }
        }
      },
      color: BENCHMARK_BY_YEAR_AREA_COLOR,
      opacity: 0.5,
      data: this.countryCodes.map(() => {
        return {
          y: this.displayUnits === 'SPECIFIC' ? specificAverage : average,
          custom: {title: 'Overall average/country', displayUnits: this.displayUnits}
        };
      })
    });

    chartData.push({
      name: `Weighted average (${this.displayUnits === 'SPECIFIC' ? formatNumber(specificWeightedAverage) : formatNumber(weightedAverage)} ${this.displayUnits === 'SPECIFIC' ? 'kg/inh' : 'tons'})`,
      type: 'line',
      legendIndex: 999,
      marker: {
        enabled: false,
        states: {
          hover: {
            enabled: false
          }
        }
      },
      color: BENCHMARK_BY_YEAR_COLORS[9],
      data: this.countryCodes.map(() => {
        return {
          y: this.displayUnits === 'SPECIFIC' ? specificWeightedAverage : weightedAverage,
          custom: {title: 'Weighted average', displayUnits: this.displayUnits}
        };
      })
    });

    return chartData;
  }


  private normalizeData(groupedData: BenchmarkData[], countryCodes: string[]): BenchmarkData[] {
    const normalizedData: BenchmarkData[] = [];
    groupedData.forEach(item => {
      const newCountryData: CountryChartData[] = [];
      countryCodes?.forEach(code => {
        let countryDataItem = item.countryData.find(c => c.countryCode === code);
        if (!countryDataItem) {
          countryDataItem = {
            countryCode: code,
            absolute: 0,
            population: 0,
            countryName: 'No data available'
          } as CountryChartData;
        }
        newCountryData.push(countryDataItem);
      });
      normalizedData.push({
        category: item.category,
        weight: item.weight,
        total: item.total,
        totalSpecific: item.totalSpecific,
        countryData: newCountryData
      });
    });
    return normalizedData;
  }

  private groupBy = (list: Array<any>, keyGetter: any) => {
    const map = new Map();
    list.forEach((item) => {
      const key = keyGetter(item);
      const collection = map.get(key);
      if (!collection) {
        map.set(key, [item]);
      } else {
        collection.push(item);
      }
    });

    const benchmarkData = [] as BenchmarkData[];
    map.forEach((value, key) => {
      let total = 0;
      let totalSpecific = 0;
      let totalWeight = 0;
      value.forEach((item: CountryChartData) => {
        total += item.absolute;
        totalSpecific += (item.absolute * 1000 / item.population);
        totalWeight += item.weight || 0;
      });
      benchmarkData.push({
        category: key,
        total,
        totalSpecific,
        weight: totalWeight,
        countryData: value

      });
    });
    return benchmarkData;
  };

}
